/**
 * @fileoverview ActionButtons component for month navigation actions.
 * Displays Current Month and Close/Open buttons.
 */

import styles from './ActionButtons.module.css';

/**
 * ActionButtons Component
 *
 * Displays action buttons for month navigation including:
 * - Current Month button (when not viewing current month)
 * - Close/Open Month button (when not viewing current month)
 * - Error message display
 *
 * Props:
 * - isCurrentMonth (boolean): Whether viewing the current month
 * - isClosed (boolean): Whether the selected month is closed
 * - isClosing (boolean): Whether a close/open operation is in progress
 * - closeError (string): Error message if close/open failed
 * - onGoToCurrentMonth (function): Called when user clicks current month button
 * - onCloseMonth (function): Called when user clicks close month button
 * - onOpenMonth (function): Called when user clicks open month button
 */
function ActionButtons({
    isCurrentMonth,
    isClosed,
    isClosing,
    closeError,
    onGoToCurrentMonth,
    onCloseMonth,
    onOpenMonth
}) {
    const handleGoToCurrentMonth = (e) => {
        e.preventDefault();
        onGoToCurrentMonth();
    };

    const handleCloseMonth = (e) => {
        e.preventDefault();
        onCloseMonth();
    };

    const handleOpenMonth = (e) => {
        e.preventDefault();
        onOpenMonth();
    };

    return (
        <div className={styles.container}>
            {!isCurrentMonth && (
                <button
                    onClick={handleGoToCurrentMonth}
                    className={styles.currentMonthButton}
                >
                    Current Month
                </button>
            )}

            {/* Close/Open Month Button - only show if not viewing current month */}
            {!isCurrentMonth && (
                <>
                    {isClosed ? (
                        <button
                            onClick={handleOpenMonth}
                            disabled={isClosing}
                            className={`${styles.openButton} ${isClosing ? styles.loading : ''}`}
                        >
                            {isClosing ? 'Opening...' : 'Open'}
                        </button>
                    ) : (
                        <button
                            onClick={handleCloseMonth}
                            disabled={isClosing}
                            className={`${styles.closeButton} ${isClosing ? styles.loading : ''}`}
                        >
                            {isClosing ? 'Closing...' : 'Close'}
                        </button>
                    )}
                </>
            )}

            {/* Error message */}
            {closeError && (
                <span className={styles.errorMessage}>
                    {closeError}
                </span>
            )}
        </div>
    );
}

export default ActionButtons;
